import React from "react";
import CookiePreferencesButton from "./CookiePreferencesButton";

/** Full Terms and Conditions — rendered on /terms and inside the terms modal. */
export const TermsContent = () => {
  return (
    <>
      <h1 className="text-2xl">Terms and Conditions</h1>
      <p>
        <strong>Effective Date:</strong> 09/30/2026
      </p>
      <p>
        Welcome to Americana &amp; Company, operated by The Americana Company
        LLC ("Company," "we," "us," or "our"). By accessing or using our website
        at americanaandcompany.com ("Site"), or by placing an order with us, you
        agree to be bound by these Terms and Conditions ("Terms"). Please read
        them carefully. If you do not agree to these Terms, you must not use our
        Site or place an order.
      </p>
      <h3>1. Company Information</h3>
      <p>
        Americana &amp; Company is an online retail platform operated by The
        Americana Company LLC, a limited liability company organized under the
        laws of the State of New Jersey. For questions or concerns, please
        contact us at:{" "}
        <a href="mailto:support@americanaandcompany.com">
          support@americanaandcompany.com
        </a>
        .
      </p>
      <h3>2. Eligibility and Use of the Site</h3>
      <p>
        - You must be at least 18 years of age to place an order or make
        purchases on this Site. If you are between 13 and 17 years of age, you
        may browse the Site only under a parent or guardian's supervision; all
        purchases must be completed by a parent or legal guardian.
        <br />
        - You agree to use the Site solely for lawful purposes and in a manner
        that does not infringe upon the rights of others or restrict their use
        of the Site.
        <br />
        - You may not use the Site to: (a) engage in any fraudulent or deceptive
        activity; (b) transmit harmful, threatening, abusive, or unlawful
        content; (c) attempt to gain unauthorized access to any portion of the
        Site or its systems; or (d) use automated tools such as bots, scrapers,
        or crawlers to access or collect data from the Site without our express
        written permission.
        <br />- We reserve the right to refuse service, suspend access, or
        cancel orders at our sole discretion, including for any violation of
        these Terms.
      </p>
      <h3>3. Orders, Pricing, and Payments</h3>
      <p>
        - Placing an order constitutes an offer to purchase. A binding contract
        is formed only upon our acceptance of your order, evidenced by an order
        confirmation or the initiation of fulfillment.
        <br />
        - We reserve the right to refuse or cancel any order at any time,
        including after an order confirmation has been sent, due to pricing
        errors, product unavailability, suspected fraud, or other reasons at our
        discretion. If we cancel a confirmed order, you will receive a full
        refund to your original payment method.
        <br />
        - While we make every effort to display accurate pricing, errors may
        occur. We are not obligated to honor an order placed at an incorrect or
        erroneous price and reserve the right to cancel such orders with a full
        refund.
        <br />
        - All prices are listed in U.S. Dollars. Applicable sales tax will be
        calculated and added at checkout based on your shipping address and
        applicable law.
        <br />
        - Payment is made at checkout using the methods available on the Site
        (including credit and debit cards, Apple Pay, and Google Pay) and is
        processed securely by Square, Inc. When you place an order, your payment
        method is authorized for the order total; the charge is collected
        shortly afterward, once the free-cancellation period described in
        Section 5 has passed. If you cancel during that period, the
        authorization is released and you are not charged.
        <br />
        - If you are signed in to an account, you may choose to save your card
        for faster checkout. Saved cards are stored by Square, Inc.; we keep
        only the card brand, last four digits, and expiration date so you can
        recognize the card. You can remove a saved card at any time from your
        account. We do not store your full payment card number, CVV, or other
        sensitive payment credentials on our servers.
        <br />- In the event of a chargeback or payment dispute, we reserve the
        right to suspend or cancel the associated order and to provide
        transaction records to the payment processor as required.
      </p>
      <h3>4. Shipping and Delivery</h3>
      <p>
        - We currently ship only to addresses within the United States.
        Available shipping options and rates are shown at checkout before you
        pay.
        <br />
        - All shipping and delivery timeframes provided are estimates only and
        are not guaranteed. We are not liable for delays caused by carriers,
        customs processing, weather events, or any other circumstances beyond
        our reasonable control.
        <br />
        - You are solely responsible for providing an accurate and complete
        shipping address at checkout. We are not responsible for orders
        delivered to an incorrect address you provided, and any costs to reship
        will be your responsibility.
        <br />- Risk of loss and title for products pass to you upon our tender
        of the items to the carrier. We are not responsible for loss, theft, or
        damage that occurs after handoff to the carrier. Claims for lost or
        damaged packages must be filed directly with the carrier; we will assist
        where reasonably possible.
      </p>
      <h3>5. Cancellations and Refunds</h3>
      <p>
        - Because all of our products are custom-printed on demand, orders may
        be canceled for a full refund only while production has not yet begun at
        our print partner. Once your item enters production, cancellation is no
        longer possible — your item is being made specifically for you. Orders
        canceled within the first few minutes after placement are always free.
        You can cancel from the order cancellation page on the Site, from your
        order confirmation email, or from your account.
        <br />- If your item arrives damaged, misprinted, or materially
        defective through no fault of your own, submit a request through the
        return request page on the Site or contact us at{" "}
        <a href="mailto:support@americanaandcompany.com">
          support@americanaandcompany.com
        </a>{" "}
        within 30 days of delivery. Include a description and clear photo of the
        issue. If we ask you to send the item back, we will email you the return
        address. Approved claims will receive a replacement or full refund at
        our discretion.
        <br />
        - Because all items are made to order, we do not accept returns or
        exchanges for change of mind, buyer's remorse, or incorrect size
        selection. Please review product descriptions and size charts carefully
        before placing your order.
        <br />
        - Refunds for approved claims will be processed to the original payment
        method within 7 business days of approval.
        <br />- We reserve the right to deny any refund request that does not
        meet the criteria set out above or that we reasonably determine to be
        fraudulent.
      </p>
      <h3>6. Accounts</h3>
      <p>
        - You may check out as a guest or create an account. Accounts use a
        one-time code sent to your email address instead of a password, so you
        are responsible for keeping access to that email account secure.
        <br />- You agree to provide accurate information and to keep it up to
        date in your account. We may suspend or close an account that is used in
        violation of these Terms.
      </p>
      <h3>7. Promotional Codes</h3>
      <p>
        - Promotional codes have no cash value, cannot be redeemed for cash or
        applied to past orders, and may not be combined unless we state
        otherwise. A code may be limited to a total number of uses, to a number
        of uses per customer, or to a time period, and we may modify or withdraw
        any code at any time.
        <br />
        - A code's use is counted when your payment is collected. If an order is
        later refunded, the use is not restored.
        <br />- We may cancel orders that use a code obtained or applied in
        violation of its terms or these Terms.
      </p>
      <h3>8. Product Reviews</h3>
      <p>
        - Only customers who purchased a product may review it. Reviews are
        moderated, and we may decline to publish or remove any review at our
        discretion. Published reviews show your first name and last initial —
        never your email address or order details.
        <br />- By submitting a review, you grant us a non-exclusive,
        royalty-free, perpetual, worldwide license to use, reproduce, display,
        and distribute it on the Site and in our marketing. You confirm that
        your review reflects your honest experience and does not infringe
        anyone's rights.
      </p>
      <h3>9. Disclaimer of Warranties</h3>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE SITE AND ALL
        PRODUCTS AND SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE"
        BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
        INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT
        WARRANT THAT THE SITE WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR FREE
        OF VIRUSES OR OTHER HARMFUL COMPONENTS. PRODUCT COLORS AND APPEARANCE
        MAY VARY SLIGHTLY FROM ON-SCREEN REPRESENTATIONS DUE TO MONITOR
        CALIBRATION AND PRINT PRODUCTION VARIATIONS; SUCH VARIATIONS DO NOT
        CONSTITUTE A DEFECT.
      </p>
      <h3>10. Limitation of Liability</h3>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE AMERICANA COMPANY
        LLC AND ITS MEMBERS, MANAGERS, OFFICERS, EMPLOYEES, AGENTS, AND
        SUPPLIERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, OR PUNITIVE DAMAGES OF ANY KIND, INCLUDING BUT NOT
        LIMITED TO LOSS OF PROFITS, LOSS OF DATA, LOSS OF GOODWILL, LOSS OF USE,
        OR BUSINESS INTERRUPTION, ARISING OUT OF OR IN CONNECTION WITH YOUR USE
        OF OR INABILITY TO USE THE SITE, YOUR PURCHASE OF PRODUCTS, OR THESE
        TERMS, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        <br />
        IN NO EVENT SHALL OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS
        ARISING OUT OF OR RELATED TO THESE TERMS EXCEED THE TOTAL AMOUNT YOU
        PAID TO US IN THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE EVENT
        GIVING RISE TO THE CLAIM.
        <br />
        SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN
        WARRANTIES OR DAMAGES, SO SOME OR ALL OF THE ABOVE EXCLUSIONS AND
        LIMITATIONS MAY NOT APPLY TO YOU. IN SUCH CASES, OUR LIABILITY WILL BE
        LIMITED TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
        <br />
        <strong>New Jersey residents:</strong> Sections 9, 10, and 11 apply to
        you only to the extent permitted by New Jersey law. Nothing in these
        Terms limits any right you have under the New Jersey Consumer Fraud Act,
        the New Jersey Truth-in-Consumer Contract, Warranty and Notice Act, or
        any other New Jersey law that cannot be waived by contract, including
        your right to recover damages for our negligence, gross negligence, or
        intentional misconduct where that law provides it.
      </p>
      <h3>11. Indemnification</h3>
      <p>
        You agree to defend, indemnify, and hold harmless The Americana Company
        LLC and its members, managers, officers, employees, agents, and
        suppliers from and against any and all claims, liabilities, damages,
        losses, costs, and expenses (including reasonable attorneys' fees)
        arising out of or in any way connected with: (a) your access to or use
        of the Site; (b) your violation of these Terms; (c) any content or
        information you submit through the Site; or (d) your violation of any
        applicable law or the rights of any third party.
      </p>
      <h3>12. Intellectual Property</h3>
      <p>
        All content on this Site — including but not limited to text, graphics,
        logos, images, product designs, and software — is owned by or licensed
        to The Americana Company LLC and is protected by U.S. and international
        copyright, trademark, and other intellectual property laws. You may not
        reproduce, distribute, modify, publicly display, or create derivative
        works from any content on this Site without our prior written consent.
        <br />
        If you believe that any content on this Site infringes your copyright,
        please send a written DMCA notice to{" "}
        <a href="mailto:support@americanaandcompany.com">
          support@americanaandcompany.com
        </a>{" "}
        with: (1) identification of the copyrighted work; (2) identification of
        the allegedly infringing material and its location on our Site; (3) your
        contact information; (4) a statement of good-faith belief; and (5) a
        statement under penalty of perjury that the information is accurate and
        that you are the copyright owner or authorized to act on their behalf.
      </p>
      <h3>13. Force Majeure</h3>
      <p>
        We shall not be liable for any failure or delay in performance of our
        obligations under these Terms due to causes beyond our reasonable
        control, including but not limited to acts of God, natural disasters,
        pandemics or public health emergencies, war, terrorism, civil unrest,
        government actions or regulations, labor disputes, supply chain
        disruptions, internet or telecommunications failures, or the failure of
        third-party service providers including carriers or print fulfillment
        partners.
      </p>
      <h3>14. Governing Law and Dispute Resolution</h3>
      <p>
        These Terms are governed by and construed in accordance with the laws of
        the State of New Jersey, without regard to its conflict of law
        principles. The United Nations Convention on Contracts for the
        International Sale of Goods does not apply.
        <br />
        <strong>Informal Resolution:</strong> Before initiating any formal
        dispute process, you agree to first contact us at
        support@americanaandcompany.com and give us 30 days to attempt an
        informal resolution.
        <br />
        <strong>Binding Arbitration:</strong> If a dispute cannot be resolved
        informally, it shall be finally resolved by binding arbitration
        administered by the American Arbitration Association ("AAA") under its
        Consumer Arbitration Rules. Any in-person hearing will take place in the
        county where you live, unless you and we agree otherwise; hearings may
        also be held by phone or video. The arbitrator's decision shall be final
        and binding, and judgment may be entered upon it in any court of
        competent jurisdiction. Either party may instead bring an individual
        claim in small claims court if it qualifies.
        <br />
        <strong>Waiver of Court and Jury Trial:</strong> BY AGREEING TO THESE
        TERMS, YOU AND WE EACH GIVE UP THE RIGHT TO GO TO COURT AND HAVE A
        DISPUTE DECIDED BY A JUDGE OR JURY, EXCEPT AS PROVIDED IN THIS SECTION.
        Disputes will instead be decided by a neutral arbitrator, and the
        grounds for appealing an arbitrator's decision are more limited than in
        court.
        <br />
        <strong>Class Action Waiver:</strong> YOU AND THE AMERICANA COMPANY LLC
        AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL
        CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS ACTION,
        CONSOLIDATED, OR REPRESENTATIVE PROCEEDING.
        <br />
        Notwithstanding the above, either party may seek injunctive or other
        equitable relief in a court of competent jurisdiction in Bergen County,
        New Jersey to prevent irreparable harm pending arbitration.
      </p>
      <h3>15. Severability and Entire Agreement</h3>
      <p>
        If any provision of these Terms is found by a court or arbitrator to be
        invalid, illegal, or unenforceable, that provision shall be modified to
        the minimum extent necessary to make it enforceable, and all remaining
        provisions shall remain in full force and effect. The failure of either
        party to enforce any right or provision of these Terms shall not
        constitute a waiver of that right or provision. These Terms, together
        with our Privacy Policy, constitute the entire agreement between you and
        The Americana Company LLC regarding your use of the Site and your
        purchases, and supersede all prior negotiations, agreements, and
        understandings between the parties relating to the same subject matter.
      </p>
      <h3>16. Changes to Terms</h3>
      <p>
        We reserve the right to update or modify these Terms at any time. Any
        changes will be posted on this page with a revised effective date. Your
        continued use of the Site after the posting of revised Terms constitutes
        your acceptance of those changes. We encourage you to review these Terms
        periodically.
      </p>
    </>
  );
};

/** Full Privacy Policy — rendered on /privacy and inside the terms modal. */
export const PrivacyContent = () => {
  return (
    <>
      <h1 className="text-2xl">Privacy Policy</h1>
      <p>
        <strong>Effective Date:</strong> 09/30/2026
      </p>
      <p>
        The Americana Company LLC ("we," "us," or "our") is committed to
        protecting your privacy. This Privacy Policy explains what personal
        information we collect, how we use and share it, how we protect it, and
        what rights you have in relation to it when you use our website at
        americanaandcompany.com ("Site").
      </p>
      <h3>1. Information We Collect</h3>
      <p>
        - <strong>Identity &amp; Contact Information:</strong> When you place an
        order or create an account, we collect your name, email address, phone
        number, shipping address, and billing address.
        <br />- <strong>Payment Information:</strong> Payment card details are
        processed directly and securely by Square, Inc. We do not receive,
        store, or have access to your full payment card number, CVV, or other
        sensitive payment credentials on our servers. If you choose to save a
        card to your account, Square stores it; we keep only its brand, last
        four digits, and expiration date so you can recognize it.
        <br />- <strong>Account Information:</strong> If you create an account,
        your email address (used to send you one-time sign-in codes) and the
        profile details you save, such as your name, phone number, and
        addresses.
        <br />- <strong>Reviews:</strong> Product reviews you submit, including
        your rating and comments. Published reviews show only your first name
        and last initial.
        <br />- <strong>Order &amp; Transaction Information:</strong> Details
        about the products you purchase, order history, cancellations, refund
        requests, and related communications.
        <br />- <strong>Technical &amp; Usage Information:</strong> IP address,
        browser type and version, device type, operating system, pages visited,
        time spent on pages, links clicked, referring URLs, and other diagnostic
        data collected automatically through cookies and similar technologies.
        <br />- <strong>Referral Information:</strong> When you arrive at the
        Site by clicking one of our own links — for example a tagged link in a
        social media post, an advertisement, or an email — we store a short
        record in your browser noting which of our links brought you here (such
        as "instagram"). This record contains no identifier, is never combined
        with information from other websites, and is not shared with anyone. We
        use it only to understand which of our own marketing efforts lead to
        orders. See Section 4 for how to prevent this.
        <br />- <strong>Communications:</strong> Messages or inquiries you send
        us via email, contact forms, or customer service interactions.
      </p>
      <h3>2. How We Use Your Information</h3>
      <p>
        We use your information to:
        <br />
        - Process, fulfill, and deliver your orders and related services.
        <br />
        - Send order confirmations, shipping notifications, and customer service
        responses.
        <br />
        - Send you promotional emails and marketing updates where you have not
        opted out. You may opt out of marketing communications at any time by
        clicking "Unsubscribe" in any email or by contacting us at
        support@americanaandcompany.com.
        <br />
        - Improve, personalize, and analyze the performance of our Site and
        customer experience.
        <br />
        - Detect, investigate, and prevent fraudulent transactions and other
        unauthorized or illegal activity.
        <br />
        - Comply with applicable legal and regulatory obligations.
        <br />- Enforce our Terms and Conditions and protect the rights,
        property, or safety of our business, customers, or others.
      </p>
      <h3>3. How We Share Your Information</h3>
      <p>
        We do not sell your personal information to third parties. We share your
        information only with trusted service providers who assist us in
        operating our business, and only to the extent necessary for those
        purposes:
        <br />- <strong>Square, Inc.</strong> — payment processing, order
        management, and transaction records.
        <br />- <strong>Printful, Inc.</strong> — print-on-demand production and
        order fulfillment. Your name, shipping address, and order details are
        shared with Printful to produce and ship your items.
        <br />- <strong>Resend</strong> — delivery of order, shipping, sign-in
        code, and other emails we send you.
        <br />- <strong>Netlify, Inc.</strong> — website hosting and storage of
        limited operational records, such as return requests and product
        reviews.
        <br />- <strong>Shipping Carriers</strong> (e.g., USPS, FedEx, DHL,
        Asendia) — to deliver your order to the address you provide.
        <br />- <strong>Address Verification Providers</strong> (Geoapify, HERE
        Technologies) — to validate and autocomplete shipping addresses at
        checkout.
        <br />- <strong>Google Analytics</strong> — aggregate Site usage
        measurement. Loaded only if you accept analytics cookies.
        <br />- <strong>Meta Platforms, Inc. (Facebook/Instagram)</strong> — we
        use the Meta Pixel to measure the performance of our advertising and to
        show ads to people who have visited our Site. It reports page views and
        purchase events (including order value and which products were bought)
        to Meta. It is loaded <strong>only if you accept cookies</strong>, is
        never loaded if you send a Global Privacy Control signal, and we have
        disabled Meta's automatic collection of information typed into forms on
        our Site. Because this supports advertising across other services, it
        may be considered "sharing" for cross-context behavioral advertising —
        see Section 8 for how to opt out.
        <br />- <strong>Legal and Regulatory Authorities</strong> — where we are
        required to disclose information by applicable law, court order,
        subpoena, or government regulation.
        <br />
        All third-party service providers are required to use your information
        solely for the purposes for which it was shared and to maintain
        appropriate confidentiality and security standards.
      </p>
      <h3>4. Cookies and Tracking Technologies</h3>
      <p>
        We use cookies and similar tracking technologies to operate and improve
        the Site. These may include:
        <br />- <strong>Essential Cookies:</strong> Strictly necessary for the
        Site to function (e.g., maintaining your session, shopping cart, and
        wishlist). These cannot be disabled without affecting core
        functionality.
        <br />- <strong>Analytics Cookies:</strong> Help us understand how
        visitors interact with our Site so we can identify issues and make
        improvements.
        <br />- <strong>Marketing &amp; Preference Cookies:</strong> Used to
        personalize content and, where applicable, serve relevant
        advertisements.
        <br />
        Third-party analytics services and other non-essential cookies are not
        loaded until you accept them in the cookie banner shown on your first
        visit, and you can change your choice at any time here:{" "}
        <CookiePreferencesButton />. You can also control or disable cookies
        through your browser settings; disabling certain cookies may affect the
        functionality of the Site.
        <br />- <strong>Referral Record:</strong> One item is stored without
        asking first: the record described in Section 1 noting which of our own
        links brought you to the Site. We treat this differently because it
        stays entirely within our own systems — it involves no third party, no
        identifier, and no tracking of your activity on any other website. If
        you decline non-essential cookies, this record is still stored, but no
        analytics service receives it and no visit is counted. To prevent it
        entirely, send a Global Privacy Control signal (below) or clear your
        browser's site data for this Site.
        <br />- <strong>Global Privacy Control (GPC):</strong> We honor the
        Global Privacy Control signal. If your browser or a browser extension
        sends a GPC signal, we treat it as a valid request to opt out of the
        sale or sharing of your personal information. We do not load
        non-essential analytics cookies, we do not store the referral record
        described above, and any referral record stored before the signal
        appeared is deleted.
      </p>
      <h3>5. Data Retention</h3>
      <p>
        We retain your personal information for as long as is necessary to
        fulfill the purposes for which it was collected, including to maintain
        your order history, comply with tax and accounting obligations, resolve
        disputes, and enforce our agreements. When your information is no longer
        required for these purposes, we will securely delete or anonymize it.
        Aggregate, non-identifying visit counts used for marketing measurement
        are retained for approximately thirteen months and then expire
        automatically. The referral record described in Section 1 is stored in
        your own browser and is removed whenever you clear your browser's site
        data. You may request earlier deletion as described in Section 7 below,
        subject to applicable legal retention requirements.
      </p>
      <h3>6. Data Security</h3>
      <p>
        We implement industry-standard technical and organizational safeguards
        to protect your personal information from unauthorized access,
        disclosure, alteration, or destruction. All payment transactions are
        encrypted using SSL/TLS and processed in compliance with Payment Card
        Industry Data Security Standards (PCI DSS) by Square, Inc.
        <br />
        While we take these precautions, no method of electronic transmission or
        storage is completely secure. In the event of a data breach that creates
        a material risk to your rights or freedoms, we will notify you as
        required by applicable law.
      </p>
      <h3>7. Your Rights</h3>
      <p>
        Depending on your jurisdiction, you may have the following rights
        regarding your personal information:
        <br />- <strong>Access &amp; Portability:</strong> Request a copy of the
        personal information we hold about you in a commonly used format.
        <br />- <strong>Correction:</strong> Request that we correct any
        inaccurate or incomplete information we hold about you.
        <br />- <strong>Deletion:</strong> Request that we delete your personal
        information, subject to certain exceptions such as legal retention
        obligations.
        <br />- <strong>Restriction:</strong> Request that we restrict
        processing of your personal information in certain circumstances.
        <br />- <strong>Opt-Out of Marketing:</strong> Withdraw consent to
        receive marketing communications at any time.
        <br />
        To exercise any of these rights, contact us at{" "}
        <a href="mailto:support@americanaandcompany.com">
          support@americanaandcompany.com
        </a>
        . We will respond within 30 days of receiving your request and may need
        to verify your identity before processing it.
      </p>
      <h3>8. California Residents — Your CCPA Rights</h3>
      <p>
        If you are a California resident, the California Consumer Privacy Act
        (CCPA) and the California Privacy Rights Act (CPRA) grant you the
        following additional rights:
        <br />- <strong>Right to Know:</strong> You may request disclosure of
        the categories and specific pieces of personal information we have
        collected about you, the sources of that information, our business or
        commercial purpose for collecting it, and the categories of third
        parties with whom we share it.
        <br />- <strong>Right to Delete:</strong> You may request deletion of
        personal information we have collected from you, subject to certain
        exceptions (e.g., where retention is required to complete a transaction
        or comply with a legal obligation).
        <br />- <strong>Right to Correct:</strong> You may request that we
        correct inaccurate personal information we maintain about you.
        <br />- <strong>Right to Opt-Out of Sale or Sharing:</strong> We do not
        sell your personal information for money. Our use of the Meta Pixel and
        analytics cookies (Section 3) may constitute "sharing" for cross-context
        behavioral advertising. You may opt out at any time by (a) sending a
        Global Privacy Control signal from your browser, which we honor
        automatically, (b) declining or resetting cookies here:{" "}
        <CookiePreferencesButton />, or (c) emailing us at the address below.
        Declining cookies prevents the Meta Pixel from loading at all.
        <br />- <strong>Right to Non-Discrimination:</strong> We will not
        discriminate against you for exercising any of your CCPA rights.
        <br />
        To submit a CCPA request, contact us at{" "}
        <a href="mailto:support@americanaandcompany.com">
          support@americanaandcompany.com
        </a>
        . We will respond within 45 days as required by law.
      </p>
      <h3>9. Children's Privacy</h3>
      <p>
        Our Site is not directed to children under the age of 13, and we do not
        knowingly collect personal information from children under 13 in
        violation of the Children's Online Privacy Protection Act (COPPA). If we
        become aware that we have inadvertently collected personal information
        from a child under 13 without verifiable parental consent, we will take
        prompt steps to delete that information. If you believe a child under 13
        has provided us with personal information, please contact us immediately
        at{" "}
        <a href="mailto:support@americanaandcompany.com">
          support@americanaandcompany.com
        </a>
        .
      </p>
      <h3>10. Third-Party Links</h3>
      <p>
        Our Site may contain links to third-party websites or services. We are
        not responsible for the privacy practices, security, or content of those
        websites. We encourage you to review the privacy policies of any
        third-party sites you visit before providing personal information.
      </p>
      <h3>11. Changes to This Policy</h3>
      <p>
        We may update this Privacy Policy from time to time to reflect changes
        in our data practices or applicable law. Updated versions will be posted
        on this page with a revised effective date. Your continued use of the
        Site after any changes constitutes your acceptance of the updated
        Policy. We encourage you to review this Policy periodically.
      </p>
      <h3>12. Contact Us</h3>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy
        Policy or our data practices, please contact us:
        <br />
        <strong>The Americana Company LLC</strong>
        <br />
        <a href="mailto:support@americanaandcompany.com">
          support@americanaandcompany.com
        </a>
      </p>
    </>
  );
};

/** Combined Terms + Privacy, used by the checkout/footer modal. */
const ToaAndReturnPolicyContent = () => {
  return (
    <>
      <TermsContent />
      <br />
      <br />
      <PrivacyContent />
    </>
  );
};

export default ToaAndReturnPolicyContent;
